import { DashboardRoutes, Routes } from '@/constants/Routes';
import { BenefitsPage } from '@/pages/CustomerDashboard/BenefitsPage';
import { BookingDetailPage } from '@/pages/CustomerDashboard/Bookings/BookingDetailPage';
import { BookingsPage } from '@/pages/CustomerDashboard/Bookings/BookingsPage';
import { CustomerDashboard } from '@/pages/CustomerDashboard/CustomerDashboard';
import { DashboardErrorBoundary } from '@/pages/CustomerDashboard/DashboardErrorBoundary';
import { FaqPage } from '@/pages/CustomerDashboard/FaqPage';
import { HowItWorksPage } from '@/pages/CustomerDashboard/HowItWorksPage';
import { PointsActivityPage } from '@/pages/CustomerDashboard/PointsActivityPage';
import { PromotionsPage } from '@/pages/CustomerDashboard/Promotions/PromotionsPage';
import { WalletPage } from '@/pages/CustomerDashboard/WalletPage';
import React, { useEffect } from 'react';
import {
  matchPath,
  PathMatch,
  Route,
  Routes as AppRoutes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useDashboard } from "@/hooks/useDashboard";
import { ErrorPage } from '@/pages/CustomerDashboard/ErrorPage';
import { CircularProgress, Container, Stack } from '@mui/material';
import { VSpacer } from '@/components/DesignSystem';
import { EnergyBenefitsPage } from '@/pages/CustomerDashboard/Energy/EnergyBenefitsPage';
import {
  EnergyCustomerDashboard,
} from '@/pages/CustomerDashboard/Energy/EnergyCustomerDashboard';
import {
  EnergyPointsActivityPage,
} from '@/pages/CustomerDashboard/Energy/EnergyPointsActivityPage';
import { useDashboardProgram } from '@/pages/CustomerDashboard/Energy/useDashboardProgram';
import { useDashboardNavigate } from '@/pages/CustomerDashboard/helpers';
import { getBrandColor } from '@/pages/CustomerDashboard/DashboardLayout';
import { DashboardDesktopShell } from '@/pages/CustomerDashboard/desktop/DashboardDesktopShell';
import { ApiEnergyDashboard } from '@api/interfaces/ApiEnergyDashboard';

const EnergyDashboardRoutes = ({ accessToken, data }: {
  accessToken: string,
  data: ApiEnergyDashboard,
}) => {
  const home = <EnergyCustomerDashboard accessToken={accessToken} data={data} />;
  const benefits = <EnergyBenefitsPage accessToken={accessToken} data={data} />;
  const activity = <EnergyPointsActivityPage accessToken={accessToken} data={data} />;
  return (
    <DashboardErrorBoundary>
      <AppRoutes>
        <Route element={home} path={Routes.CUSTOMER_DASHBOARD} />
        <Route element={home} path={Routes.CUSTOMER_DASHBOARD_ALT} />
        <Route element={benefits} path={Routes.CUSTOMER_DASHBOARD_BENEFITS} />
        <Route element={benefits} path={Routes.CUSTOMER_DASHBOARD_BENEFITS_ALT} />
        <Route element={activity} path={Routes.CUSTOMER_DASHBOARD_POINTS_ACTIVITY} />
        <Route element={activity} path={Routes.CUSTOMER_DASHBOARD_POINTS_ACTIVITY_ALT} />
        <Route element={<RedirectToDashboardHome accessToken={accessToken} />} path="*" />
      </AppRoutes>
    </DashboardErrorBoundary>
  );
};

// Promotions, wallet, bookings and the help pages load agronomy data, so energy mode sends
// the customer back to the energy dashboard.
const RedirectToDashboardHome = ({ accessToken }: { accessToken: string }) => {
  const navigateToPage = useDashboardNavigate();
  useEffect(() => {
    navigateToPage(Routes.CUSTOMER_DASHBOARD, accessToken);
  }, []);
  return null;
};

export const DashboardView = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const match = DashboardRoutes.reduce<PathMatch<'*' | 'accessToken'> | null>(
    (match, pattern) => (match ? match : matchPath(pattern, location.pathname)),
    null,
  );

  const accessToken = searchParams.get('accessToken') ?? match?.params.accessToken;
  const {
    energyData,
    isLoading: isProgramLoading,
    mode,
  } = useDashboardProgram(accessToken);
  const isEnergyMode = mode === 'energy' && !!energyData && !!accessToken;
  const { data: dashboardData, isLoading, isError } = useDashboard(
    isProgramLoading || isEnergyMode ? undefined : accessToken,
  );

  if (isProgramLoading || isLoading) {
    return (
      <DashboardDesktopShell>
        <Container>
          <Stack alignItems="center">
            <VSpacer size="14" />
            <CircularProgress />
          </Stack>
        </Container>
      </DashboardDesktopShell>
    );
  }

  if (isEnergyMode) {
    return (
      <DashboardDesktopShell brandColor={getBrandColor(energyData.retailer)}>
        {energyData.farmer.isHistorical
          ? <ErrorPage />
          : <EnergyDashboardRoutes accessToken={accessToken} data={energyData} />}
      </DashboardDesktopShell>
    );
  }

  if (isError) {
    return (
      <DashboardDesktopShell>
        <ErrorPage />
      </DashboardDesktopShell>
    );
  }

  if (dashboardData && dashboardData.farmer.isHistorical) {
    return (
      <DashboardDesktopShell brandColor={getBrandColor(dashboardData.retailer)}>
        <ErrorPage />
      </DashboardDesktopShell>
    );
  }

  return (
    <DashboardDesktopShell
      brandColor={dashboardData ? getBrandColor(dashboardData.retailer) : undefined}
    >
      <DashboardErrorBoundary>
        <AppRoutes>
          <Route element={<CustomerDashboard />} path={Routes.CUSTOMER_DASHBOARD} />
          <Route element={<CustomerDashboard />} path={Routes.CUSTOMER_DASHBOARD_ALT} />
          <Route element={<BenefitsPage />} path={Routes.CUSTOMER_DASHBOARD_BENEFITS} />
          <Route element={<BenefitsPage />} path={Routes.CUSTOMER_DASHBOARD_BENEFITS_ALT} />
          <Route element={<FaqPage />} path={Routes.CUSTOMER_DASHBOARD_FAQ} />
          <Route element={<FaqPage />} path={Routes.CUSTOMER_DASHBOARD_FAQ_ALT} />
          <Route element={<HowItWorksPage />} path={Routes.CUSTOMER_DASHBOARD_HOW_IT_WORKS_ALT} />
          <Route element={<HowItWorksPage />} path={Routes.CUSTOMER_DASHBOARD_HOW_IT_WORKS} />
          <Route element={<WalletPage />} path={Routes.CUSTOMER_DASHBOARD_WALLET} />
          <Route element={<WalletPage />} path={Routes.CUSTOMER_DASHBOARD_WALLET_ALT} />
          <Route
            element={<PointsActivityPage />}
            path={Routes.CUSTOMER_DASHBOARD_POINTS_ACTIVITY}
          />
          <Route
            element={<PointsActivityPage />}
            path={Routes.CUSTOMER_DASHBOARD_POINTS_ACTIVITY_ALT}
          />
          <Route element={<PromotionsPage />} path={Routes.CUSTOMER_DASHBOARD_PROMOTIONS} />
          <Route element={<PromotionsPage />} path={Routes.CUSTOMER_DASHBOARD_PROMOTIONS_ALT} />
          <Route element={<BookingDetailPage />} path={Routes.CUSTOMER_DASHBOARD_BOOKING_DETAIL} />
          <Route
            element={<BookingDetailPage />}
            path={Routes.CUSTOMER_DASHBOARD_BOOKING_DETAIL_ALT}
          />
          <Route element={<BookingsPage />} path={Routes.CUSTOMER_DASHBOARD_BOOKINGS} />
          <Route element={<BookingsPage />} path={Routes.CUSTOMER_DASHBOARD_BOOKINGS_ALT} />
        </AppRoutes>
      </DashboardErrorBoundary>
    </DashboardDesktopShell>
  );
};
